import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2, UserCheck } from "lucide-react";

const HIRE_TYPES = ["Intern", "Full-time", "Part-time", "Contract"];
const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

function displayName(c) {
  return [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ");
}

function Field({ label, children, hint }) {
  return (
    <label className="block text-xs text-slate-600 space-y-1">
      <span className="font-medium">{label}</span>
      {children}
      {hint ? <span className="block text-[11px] text-slate-500">{hint}</span> : null}
    </label>
  );
}

export default function CandidateHireModal({ candidate, departments, designations, employees, onClose, onDone }) {
  const [form, setForm] = useState({
    employment_type: candidate.intended_employment_type || "Full-time",
    new_code: "",
    joining_date: candidate.proposed_joining_date || new Date().toISOString().slice(0, 10),
    department_id: candidate.department_id || "",
    designation_id: candidate.designation_id || "",
    annual_ctc: candidate.annual_ctc || 0,
    manager_id: "",
    work_location: candidate.work_location || "",
    location_id: candidate.location_id || "",
    status: "Onboarding",
    official_email: (candidate.email || "").trim(),
  });
  const [codeAuto, setCodeAuto] = useState(true);
  const [codeLoading, setCodeLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const codeFetchSeq = useRef(0);

  const companyId = candidate.company_id || "company";

  const fetchNextCode = useCallback(async (employmentType) => {
    const seq = ++codeFetchSeq.current;
    setCodeLoading(true);
    try {
      const r = await api.get("/employees/next-code", {
        params: {
          employment_type: employmentType || "Full-time",
          company_id: companyId,
        },
      });
      if (seq !== codeFetchSeq.current) return;
      const code = r.data?.code || "";
      if (code) {
        setForm((f) => ({ ...f, new_code: code }));
        setCodeAuto(true);
        setErr((e) => (e && String(e).includes("next employee code") ? "" : e));
      } else {
        setErr("Could not load next employee code");
      }
    } catch (e) {
      if (seq !== codeFetchSeq.current) return;
      setErr(e?.response?.data?.detail || "Could not load next employee code");
    } finally {
      if (seq === codeFetchSeq.current) setCodeLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!codeAuto) return;
    fetchNextCode(form.employment_type);
  }, [codeAuto, form.employment_type, fetchNextCode]);

  const submit = async () => {
    setBusy(true);
    setErr("");
    const phone = (candidate.phone || "").trim();
    const personalEmail = (candidate.personal_email || "").trim();
    const officialEmail = (form.official_email || "").trim().toLowerCase();
    const pan = (candidate.pan || "").replace(/\s+/g, "").toUpperCase();
    const aadhaar = (candidate.aadhaar || "").replace(/\D+/g, "");
    if (!phone || phone.replace(/\D+/g, "").length < 10) {
      setErr("Candidate phone is required before hire — edit the candidate first.");
      setBusy(false);
      return;
    }
    if (!personalEmail) {
      setErr("Candidate personal email is required before hire — edit the candidate first.");
      setBusy(false);
      return;
    }
    if (!officialEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(officialEmail)) {
      setErr("Official / login email is required to create the employee login.");
      setBusy(false);
      return;
    }
    if (!pan && !aadhaar) {
      setErr("Candidate needs PAN or Aadhaar before hire — edit the candidate first.");
      setBusy(false);
      return;
    }
    try {
      const code = (form.new_code || "").trim();
      const payload = {
        ...form,
        official_email: officialEmail,
        annual_ctc: Number(form.annual_ctc || 0),
        manager_id: form.manager_id || null,
        department_id: form.department_id || null,
        designation_id: form.designation_id || null,
        location_id: form.location_id || null,
      };
      if (codeAuto || !code) delete payload.new_code;
      else payload.new_code = code;
      const res = await api.post(`/candidates/${candidate.id}/hire`, payload);
      await onDone(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Hire failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 space-y-4" data-testid="hire-candidate-modal">
        <h3 className="text-lg font-semibold text-slate-800">Hire Candidate → Employee</h3>
        <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm">
          <span className="font-semibold">{displayName(candidate)}</span>
          <span className="text-slate-500"> · {candidate.code}</span>
          <div className="text-xs text-slate-500 mt-1">Prefill from candidate — change only if needed.</div>
        </div>
        {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Official / Login Email *"
            hint="Creates the employee login. Prefills if already on the candidate."
          >
            <input
              type="email"
              className={inputCls}
              value={form.official_email}
              onChange={(e) => setForm({ ...form, official_email: e.target.value })}
              data-testid="hire-official-email"
              placeholder="name@company.com"
            />
          </Field>
          <Field label="Hire as *">
            <select
              className={inputCls}
              value={form.employment_type}
              onChange={(e) => {
                const t = e.target.value;
                setCodeAuto(true);
                setForm({ ...form, employment_type: t });
                fetchNextCode(t);
              }}
              data-testid="hire-employment-type"
            >
              {HIRE_TYPES.map((t) => (
                <option key={t} value={t}>{t === "Full-time" ? "Full-time (Permanent)" : t}</option>
              ))}
            </select>
          </Field>
          <Field label="Employee code">
            <div className="flex gap-2">
              <input
                className={inputCls}
                value={codeLoading ? "…" : form.new_code}
                disabled={codeLoading}
                onChange={(e) => { setCodeAuto(false); setForm({ ...form, new_code: e.target.value }); }}
                data-testid="hire-code"
              />
              <button
                type="button"
                className="px-2 text-xs border rounded inline-flex items-center gap-1 disabled:opacity-50"
                disabled={codeLoading}
                onClick={() => { setCodeAuto(true); fetchNextCode(form.employment_type); }}
              >
                {codeLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                Auto
              </button>
            </div>
          </Field>
          <Field label="Joining Date *">
            <input type="date" className={inputCls} value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="Onboarding">Onboarding</option>
              <option value="Active">Active</option>
              <option value="Probation">Probation</option>
            </select>
          </Field>
          <Field label="Department *">
            <select className={inputCls} value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value, designation_id: "" })}>
              <option value="">—</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Designation *">
            <select className={inputCls} value={form.designation_id} onChange={(e) => setForm({ ...form, designation_id: e.target.value })}>
              <option value="">—</option>
              {designations.filter((d) => !form.department_id || d.department_id === form.department_id).map((d) => (
                <option key={d.id} value={d.id}>{d.title}</option>
              ))}
            </select>
          </Field>
          <Field label="Annual CTC (₹)">
            <input type="number" min="0" className={inputCls} value={form.annual_ctc} onChange={(e) => setForm({ ...form, annual_ctc: e.target.value })} />
          </Field>
          <Field label="Reporting Manager">
            <select className={inputCls} value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
              <option value="">—</option>
              {employees.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.basic?.first_name} {m.basic?.last_name} ({m.basic?.code})
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800 space-y-1">
          <div>✓ EMP / INT / CON / PT assigned only at hire</div>
          <div>✓ Offer letters remount + documents copy to employee</div>
          <div>✓ Convert Intern → Permanent unchanged</div>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs border rounded">Cancel</button>
          <button
            type="button"
            disabled={busy || !form.department_id || !form.designation_id}
            onClick={submit}
            data-testid="hire-submit-btn"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
            Hire
          </button>
        </div>
      </div>
    </div>
  );
}
